import React from 'react';
import { GameCard } from '../../../Games/GameCard';
import { GameCardContainerType } from '../../../Games/GameCard/GameCard.types';
import { RowCollectionLayout } from '../layouts/types';
import { Game, GameItemResponse, GameChannel, GameType, BingoItemResponse } from '../../../Games/games.model';
import { transformGameData } from '../../../../utils/games/games.utils';
import { getImageUrl } from '../../../../utils/navigation';
import { ItvCollectionType } from '../../types';
import { GetBingoPageDataQuery } from '../../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/get-bingo-page-data.query.generated';
import { GetSpinsPageDataQuery } from '../../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/get-spins-page-data.query.generated';
import { APP_URLS } from '../../../../consts';
import { PlatformPromos } from '../../../../hooks/useFetchPlatformPromos/types';
import { evaluatePromosWithPlatform, isAnyPlatformPromoReceived } from './platformPromos';
import { ItvPromoCard } from '../../ItvPromoCard';
import { BingoCard, BingoData } from '../../BingoCard';
import { ParsedPromo, PromoTypes } from '../../../Layout/Promotions/types';
import { JackPots } from '../../../Games/service';
import { isAfter, isBefore, parseISO } from 'date-fns';
import { PROMOTION, today } from '../../../Layout/Promotions/consts';
import { BingoGameSnapshotSubscription } from '../../../../libs/graphql/baseAppAPI/subscriptions/__generated__/bingo-data-udapted.generated';

type PageDataWithCarousel = GetBingoPageDataQuery['pageData'][number] | GetSpinsPageDataQuery['pageData'][number];
type CarouselCollections = NonNullable<NonNullable<PageDataWithCarousel['collections']>[number]>;
type CollectionItem = NonNullable<CarouselCollections['Collections_id']>;
type PromoItems = CollectionItem['promotions'];
type PromoItem = NonNullable<NonNullable<NonNullable<PromoItems>[number]>['magicx_promotions_id']>;
type CollectionGameEntry = {
  game_id?: GameItemResponse | null;
} | null;
type CollectionBingoEntry = {
  bingos_id?: BingoItemResponse | null;
} | null;

export type CollectionGames = Array<CollectionGameEntry> | null;
export type CollectionBingos = Array<CollectionBingoEntry> | null;

type BuildTilesInput = {
  collectionId?: string;
  collectionType?: string | null;
  layoutType?: RowCollectionLayout;
  games?: CollectionGames;
  promotions?: PromoItems;
  bingos?: CollectionBingos;
  recentlyPlayedGames?: GameItemResponse[];
  fallbackImages: string[];
  stubImageClassName?: string;
  platformPromos?: PlatformPromos;
  handleClaimClick?: (promo?: ParsedPromo) => void;
  jackPots?: JackPots;
  disableMenu?: boolean;
  bingoRealTimeData?: Record<string, BingoGameSnapshotSubscription | null>;
  now?: number;
  isMobile?: boolean;
};

const layoutContainerPattern: Partial<Record<RowCollectionLayout, GameCardContainerType[]>> = {
  'hero-1': [GameCardContainerType.Wide],
  'hero-2': [GameCardContainerType.Wide, GameCardContainerType.Wide],
  'row-3': [GameCardContainerType.Box, GameCardContainerType.Box, GameCardContainerType.Box],
  'row-5': [
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
  ],
  'row-6': [
    GameCardContainerType.Tall,
    GameCardContainerType.Tall,
    GameCardContainerType.Tall,
    GameCardContainerType.Tall,
    GameCardContainerType.Tall,
    GameCardContainerType.Tall,
  ],
  'row-6-box': [
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
  ],
  'grid-6': [
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
  ],
  'grid-2x2': [
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
  ],
  'grid-5x5': [GameCardContainerType.Box],
  'wide+grid3x3': [
    GameCardContainerType.Wide,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
  ],
  'square+grid3x2': [
    GameCardContainerType.Box,
    GameCardContainerType.Wide,
    GameCardContainerType.Wide,
    GameCardContainerType.Wide,
    GameCardContainerType.Wide,
    GameCardContainerType.Wide,
    GameCardContainerType.Wide,
  ],
  'tall+grid2x2': [
    GameCardContainerType.Tall,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Tall,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
  ],
  'hero-double-tall-grid': [
    GameCardContainerType.Box,
    GameCardContainerType.Tall,
    GameCardContainerType.Tall,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
  ],
};

// Mobile-specific patterns - when defined, these override the default pattern on mobile
const mobileLayoutContainerPattern: Partial<Record<RowCollectionLayout, GameCardContainerType[]>> = {
  'square+grid3x2': [
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
  ],
};

export const getRequiredItemsCount = (layoutType: RowCollectionLayout | undefined): number => {
  if (!layoutType) return 0;
  const pattern = layoutContainerPattern[layoutType];

  return pattern?.length || 0;
};

const getContainerTypeForIndex = (
  layoutType: RowCollectionLayout | undefined,
  index: number,
  isMobile?: boolean
): GameCardContainerType => {
  if (!layoutType) return GameCardContainerType.Box;

  // Use mobile-specific pattern if available and isMobile is true
  const mobilePattern = isMobile ? mobileLayoutContainerPattern[layoutType] : null;
  const pattern = mobilePattern ?? layoutContainerPattern[layoutType];

  if (!pattern || pattern.length === 0) return GameCardContainerType.Box;

  const result = pattern[Math.min(index, pattern.length - 1)] ?? GameCardContainerType.Box;

  return result;
};

const createMockGame = (imageUrl: string, index: number): Game => {
  const displayName = `Featured game ${index + 1}`;
  const friendlyUrlName = `featured-game-${index + 1}`;

  return {
    id: `mock-game-${index}`,
    gameType: GameType.Game,
    gameId: `mock-game-${index}`,
    displayName,
    friendlyUrlName,
    categoryName: 'itv-collection',
    shortDescription: '',
    longDescription: '',
    rules: '',
    bannerImage: { __typename: 'directus_files', imgUrl: imageUrl, width: null, height: null },
    gameplayBackground: { __typename: 'directus_files', imgUrl: imageUrl, width: null, height: null },
    gameImage: { __typename: 'directus_files', imgUrl: imageUrl, width: null, height: null },
    gameImageBox: { __typename: 'directus_files', imgUrl: imageUrl, width: null, height: null },
    gameImageTall: { __typename: 'directus_files', imgUrl: imageUrl, width: null, height: null },
    gameImageWide: { __typename: 'directus_files', imgUrl: imageUrl, width: null, height: null },
    channels: [
      {
        name: 'Desktop',
        channel: GameChannel.Desktop,
        width: 16,
        height: 9,
        isAvailableForDemoPlay: false,
      },
      {
        name: 'Mobile',
        channel: GameChannel.Mobile,
        width: 9,
        height: 16,
        isAvailableForDemoPlay: false,
      },
    ],
    provider: { mediatorId: '' },
    features: [],
    tags: [],
    rating: null,
    rtp: null,
    paylines: null,
    maxBet: null,
    minBet: null,
    maxRtp: null,
    minRtp: null,
    netPosTransactionCount: null,
    netPosDelay: null,
    brand: 'spins',
    SEO_description: '',
    SEO_title: '',
  };
};

const normalizeGame = (gameNode: GameItemResponse | null | undefined, fallbackImage: string, index: number): Game => {
  const transformedGame = transformGameData(gameNode);

  if (transformedGame) {
    return transformedGame;
  }

  return createMockGame(fallbackImage, index);
};

const renderGameTile = (
  game: Game,
  layoutType: RowCollectionLayout | undefined,
  index: number,
  keyPrefix: string,
  fallbackImage: string,
  containerWidth?: number,
  jackPots?: JackPots,
  disableMenu?: boolean,
  isMobile?: boolean
): JSX.Element => (
  <GameCard
    key={`${keyPrefix}-game-${game.id}-${index}`}
    game={game}
    containerType={getContainerTypeForIndex(layoutType, index, isMobile)}
    fallbackImageAltText={game.displayName}
    fallbackImageUrl={fallbackImage}
    showGameName={false}
    disableMenu={disableMenu}
    containerElementWidth={containerWidth}
    gameUrl={game.friendlyUrlName}
    jackPots={jackPots}
  />
);

const buildGameCardTiles = ({
  collectionId,
  layoutType,
  games,
  fallbackImages,
  jackPots,
  disableMenu,
  isMobile,
}: BuildTilesInput): JSX.Element[] => {
  const safeFallbacks = fallbackImages.length ? fallbackImages : [''];
  const keyPrefix = collectionId ?? 'collection';
  const containerWidth = typeof window !== 'undefined' ? window.innerWidth : undefined;

  if (!games?.length) {
    return safeFallbacks.map((src, index) =>
      renderGameTile(
        createMockGame(src, index),
        layoutType,
        index,
        keyPrefix,
        src,
        containerWidth,
        undefined,
        disableMenu,
        isMobile
      )
    );
  }

  return games
    .filter((entry) => entry?.game_id)
    .map((entry, index) => {
      const fallbackImage = safeFallbacks[index % safeFallbacks.length] || '';
      const normalizedGame = normalizeGame(entry?.game_id, fallbackImage, index);

      return renderGameTile(
        normalizedGame,
        layoutType,
        index,
        keyPrefix,
        fallbackImage,
        containerWidth,
        jackPots,
        disableMenu,
        isMobile
      );
    });
};

const buildPromoCardTiles = ({
  promotions,
  platformPromos,
  fallbackImages,
  layoutType,
  handleClaimClick,
  isMobile,
}: BuildTilesInput): (JSX.Element | null)[] =>
  (promotions || [])
    .filter((cmsPromo) => {
      const promo = cmsPromo?.magicx_promotions_id as PromoItem;

      if (!promo) return false;
      const startDateTime = parseISO(`${promo.start_date}T${promo.start_time || '00:00:00'}`);

      if (!promo.end_date) {
        return isAfter(today, startDateTime);
      }

      const endDateTime = parseISO(`${promo.end_date}T${promo.end_time || '23:59:59'}`);

      return isAfter(today, startDateTime) && isBefore(today, endDateTime);
    })
    ?.map((cmsPromo, index) => {
      const promo = cmsPromo?.magicx_promotions_id as PromoItem;
      const promoWithReferralFlag = promo as PromoItem & { prize_refer?: boolean | null };
      const isPlatformPromoReceived = isAnyPlatformPromoReceived(platformPromos);
      const WIDE_LAYOUT: RowCollectionLayout = 'hero-1';

      const parsedPromo = {
        id: promo.id,
        startDate: `${promo.start_date}T${promo.start_time || '00:00:00'}`,
        endDate: `${promo.end_date}T${promo.end_time || '23:59:59'}`,
        bonusType: String(promo.__typename),
        title: String(promo.translations?.[0]?.title),
        subtitle: String(promo.translations?.[0]?.subtitle || ''),
        description: String(promo.translations?.[0]?.description),
        ctaCopy: promo.translations?.[0]?.cta_copy,
        ctaUrl: String(promo.translations?.[0]?.cta_url),
        bonusId: promo.bonus_ID,
        tagId: promo.tag_ID,
        promoType: promo.bonus_type?.toLowerCase(),
        dmBonusId: promo.DM_Template_ID,
        dmMaxAmount: promo.DM_Max_Amount,
        rectImg: getImageUrl(promo.thumbnail?.filename_disk),
        longImg: getImageUrl(promo.thumbnail_wide?.filename_disk),
        tallImg: getImageUrl(promo.thumbnail_tall?.filename_disk),
        imgAlt: promo.thumbnail?.title || PROMOTION,
        pageUrl: `${APP_URLS.itv.promotionRoot}/${promo.translations?.[0]?.slug}`,
        successCTAGameInformation: {
          gameId: '',
        },
        magicXSlug: promo.magicx_slug,
        prizeRefer: Boolean(promoWithReferralFlag.prize_refer),
      };

      const networkPromo =
        (parsedPromo.promoType === PromoTypes.NETWORK || parsedPromo.promoType === PromoTypes.PRIZE) &&
        !parsedPromo.tagId
          ? parsedPromo
          : null;

      const compiledPromo = isPlatformPromoReceived
        ? evaluatePromosWithPlatform(parsedPromo, platformPromos as PlatformPromos)
        : networkPromo;

      return compiledPromo ? (
        <ItvPromoCard
          key={promo.id}
          handleClaimClick={handleClaimClick}
          promo={compiledPromo}
          isWide={layoutType === WIDE_LAYOUT}
          type={getContainerTypeForIndex(layoutType, Number(index), isMobile)}
          layout={layoutType}
        />
      ) : null;
    })
    .filter(Boolean);

const buildStubTiles = ({
  collectionId,
  fallbackImages,
  stubImageClassName,
}: Pick<BuildTilesInput, 'collectionId' | 'fallbackImages' | 'stubImageClassName'>): JSX.Element[] => {
  const className = stubImageClassName ?? 'itv-row-collection__stub-image';
  const keyPrefix = collectionId ?? 'collection';

  return fallbackImages.map((src, index) => (
    <img key={`${keyPrefix}-stub-${index}`} src={src} alt={`Collection item ${index + 1}`} className={className} />
  ));
};

const buildRecentlyPlayedGamesTiles = (options: BuildTilesInput): JSX.Element[] => {
  const { recentlyPlayedGames } = options;

  // Adapt GameItemResponse[] to CollectionGames format to reuse buildGameCardTiles
  const adaptedGames: CollectionGames = recentlyPlayedGames?.map((game) => ({ game_id: game })) ?? null;

  return buildGameCardTiles({
    ...options,
    games: adaptedGames,
  });
};

export const buildCollectionTiles = (options: BuildTilesInput): (JSX.Element | null)[] => {
  const { collectionType } = options;

  if (collectionType === ItvCollectionType.GAMES) {
    return buildGameCardTiles(options);
  }

  if (collectionType === ItvCollectionType.PROMOTIONS) {
    return buildPromoCardTiles(options);
  }

  if (collectionType === ItvCollectionType.BINGO) {
    return buildBingoTiles(options);
  }

  if (collectionType === ItvCollectionType.BINGO_GAMES) {
    return buildBingoGamesTiles(options);
  }

  if (collectionType === ItvCollectionType.RECENTLY_PLAYED_GAMES) {
    return buildRecentlyPlayedGamesTiles(options);
  }

  return buildStubTiles(options);
};

function buildBingoTiles(options: BuildTilesInput): (JSX.Element | null)[] {
  const { bingos, layoutType, now = 0, isMobile } = options;
  const [firstBingo, ...restBingos] = bingos || [];
  const getGameStartEpoch = (bingoEntry: CollectionBingoEntry): number | null => {
    const roomId = bingoEntry?.bingos_id?.roomID;

    if (!roomId) return null;

    const startEpoch = options.bingoRealTimeData?.[roomId]?.bingoGameSnapshot?.gameStartEpoch || null;

    return startEpoch;
  };
  const sortedBingos = [
    firstBingo,
    ...restBingos.sort((a, b) => {
      const aStart = getGameStartEpoch(a);
      const bStart = getGameStartEpoch(b);

      if (aStart === null && bStart === null) return 0;

      if (aStart === null) return 1;

      if (bStart === null) return -1;

      return aStart - bStart;
    }),
  ];

  return (
    sortedBingos.map((bingoEntry, index) => {
      const bingo = bingoEntry?.bingos_id;

      if (!bingo) return null;
      const [translations] = bingo.translations || [];

      if (!translations) return null;
      const realtimeData = options.bingoRealTimeData?.[bingo.roomID];

      const bingoData: BingoData = {
        id: bingo.id || `bingo-${index}`,
        hasRealTimeData: !!realtimeData,
        roomID: bingo.roomID || `room-${index}`,
        number_of_balls: bingo.number_of_balls || 90,
        min_bet: bingo.min_bet || '0.10',
        max_bet: bingo.max_bet || '1.00',
        img: bingo.img,
        bannerImage: bingo.img_banner,
        imgBox: bingo.img_box,
        imgWide: bingo.img_wide,
        title: translations.display_name ?? '',
        slug: translations.slug ?? '',
        ctaText: translations.cta_text ?? 'Play',

        prize: realtimeData?.bingoGameSnapshot?.simplePrizePotAmount,
        jackpot: realtimeData?.bingoGameSnapshot?.jackpotAmount,
        players: realtimeData?.bingoGameSnapshot?.playersInGame?.toString(),
        timeToStart: realtimeData?.bingoGameSnapshot?.gameStartEpoch - now,
        cardCost: realtimeData?.bingoGameSnapshot?.cardCost,
      };

      return (
        <BingoCard
          key={`bingo-${bingo.id}-${index}`}
          bingo={bingoData}
          type={getContainerTypeForIndex(layoutType, index, isMobile)}
        />
      );
    }) || []
  );
}

function buildBingoGamesTiles(options: BuildTilesInput): (JSX.Element | null)[] {
  const { bingos, games, layoutType, collectionId, fallbackImages, isMobile } = options;
  const safeFallbacks = fallbackImages.length ? fallbackImages : [''];
  const keyPrefix = collectionId ?? 'collection';
  const containerWidth = typeof window !== 'undefined' ? window.innerWidth : undefined;
  const tiles: (JSX.Element | null)[] = [];

  // First tile: Use the first bingo item for the wide tile (index 0)
  const firstBingo = bingos?.[0]?.bingos_id;

  if (firstBingo) {
    const realtimeData = options.bingoRealTimeData?.[firstBingo.roomID];
    const [translations] = firstBingo.translations || [];

    if (translations) {
      const bingoData: BingoData = {
        hasRealTimeData: !!realtimeData,
        id: firstBingo.id || 'bingo-wide',
        roomID: firstBingo.roomID || 'room-wide',
        number_of_balls: firstBingo.number_of_balls || 90,
        min_bet: firstBingo.min_bet || '£0.10',
        max_bet: firstBingo.max_bet || '£1.00',
        img: firstBingo.img,
        bannerImage: firstBingo.img_banner,
        imgBox: firstBingo.img_box,
        imgWide: firstBingo.img_wide,
        title: translations.display_name ?? '',
        slug: translations.slug ?? '',
        ctaText: translations.cta_text ?? 'Play',

        prize: realtimeData?.bingoGameSnapshot?.simplePrizePotAmount,
        jackpot: realtimeData?.bingoGameSnapshot?.jackpotAmount,
        players: realtimeData?.bingoGameSnapshot?.playersInGame?.toString(),
        cardCost: realtimeData?.bingoGameSnapshot?.cardCost,
      };

      tiles.push(
        <BingoCard
          key={`${keyPrefix}-bingo-wide-${firstBingo.id}`}
          bingo={bingoData}
          type={getContainerTypeForIndex(layoutType, 0, isMobile)}
        />
      );
    }
  } else {
    // Fallback: create mock game for wide tile if no bingo available
    const fallbackImage = safeFallbacks[0] || '';
    const mockGame = createMockGame(fallbackImage, 0);

    tiles.push(
      renderGameTile(mockGame, layoutType, 0, keyPrefix, fallbackImage, containerWidth, undefined, undefined, isMobile)
    );
  }

  // Remaining tiles: Use games for the grid positions (indices 1-6)
  const gridTileCount = getRequiredItemsCount(layoutType) - 1; // -1 because first tile is bingo

  for (let i = 0; i < gridTileCount; i++) {
    const gameIndex = i;
    const tileIndex = i + 1; // +1 because index 0 is the bingo tile
    const gameEntry = games?.[gameIndex];
    const fallbackImage = safeFallbacks[gameIndex % safeFallbacks.length] || '';

    if (gameEntry?.game_id) {
      const normalizedGame = normalizeGame(gameEntry.game_id, fallbackImage, gameIndex);

      tiles.push(
        renderGameTile(
          normalizedGame,
          layoutType,
          tileIndex,
          keyPrefix,
          fallbackImage,
          containerWidth,
          undefined,
          undefined,
          isMobile
        )
      );
    } else {
      // Create mock game as fallback
      const mockGame = createMockGame(fallbackImage, gameIndex);

      tiles.push(
        renderGameTile(
          mockGame,
          layoutType,
          tileIndex,
          keyPrefix,
          fallbackImage,
          containerWidth,
          undefined,
          undefined,
          isMobile
        )
      );
    }
  }

  return tiles;
}
